import { useCallback } from 'react';
import useApi from './useApi';
import { useAppSettings } from '../hooks/useAppSettings';
import { CoatingManufacturer } from './useGetCoatingManufacturers';

export interface CoatingManufacturerUpdateResponse {
    success: boolean;
    data: any;
    message: string;
}

/**
 * Custom hook for updating coating manufacturer data
 * Uses POST endpoint: /UpsertCoatingManufacturer
 */
const useUpdateCoatingManufacturer = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a coating manufacturer via the UpsertCoatingManufacturer API endpoint
     * @param manufacturer - Complete coating manufacturer data
     * @returns Promise<CoatingManufacturerUpdateResponse>
     */
    const updateCoatingManufacturer = useCallback(
        async (manufacturer: CoatingManufacturer): Promise<CoatingManufacturerUpdateResponse> => {
            if (!manufacturer || manufacturer.id === undefined || manufacturer.id === null) {
                throw new Error('Coating manufacturer data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const endpoint = `${apiUrl}UpsertCoatingManufacturer`;

                // Convert camelCase to PascalCase for API
                const apiPayload = {
                    Id: manufacturer.id,
                    Name: manufacturer.name,
                    LastUpdatedUtc: new Date().toISOString(),
                };

                const response = await postApiData({
                    endpoint,
                    data: apiPayload,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Coating manufacturer updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update coating manufacturer:', error);

                const errorMessage =
                    error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update coating manufacturer';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    return {
        updateCoatingManufacturer,
        isLoading: state.isLoading,
        error: state.error,
    };
};

export default useUpdateCoatingManufacturer;
